<template>
  <form>
    <div class="px-6">
      <div class="text-sm text-sonicSilver font-light mb-4">
        Поля, отмеченные знаком <span class="text-trueBlue">*</span>
        обязательны для заполнения
      </div>

      <div class="mb-2 w-5/6">
        <BaseLabel for-id="fio">
          ФИО <span class="text-trueBlue">*</span>
        </BaseLabel>
        <BaseInput
          v-model="formData.fio"
          id="fio"
          :hasError="hasFormError('fio')"
          :disabled="isDisabled('fio')"
        />
        <BaseError v-if="hasFormError('fio')">
          {{ getFormError('fio') }}
        </BaseError>
      </div>
      <div class="mb-2 w-5/6">
        <BaseLabel for-id="position">
          Должность <span class="text-trueBlue">*</span>
        </BaseLabel>
        <BaseInput
          v-model="formData.position"
          id="position"
          :hasError="hasFormError('position')"
          :disabled="isDisabled('position')"
        />
        <BaseError v-if="hasFormError('position')">
          {{ getFormError('position') }}
        </BaseError>
      </div>

      <div class="mb-2 flex flex-row w-5/6">
        <div class="mr-6 w-1/3">
          <BaseLabel for-id="email">
            Email <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseInput
            v-model="formData.email"
            id="email"
            :hasError="hasFormError('email')"
            :disabled="isDisabled('email')"
          />
          <BaseError v-if="hasFormError('email')">
            {{ getFormError('email') }}
          </BaseError>
        </div>
        <div class="mr-6 w-1/3">
          <BaseLabel for-id="phone">
            Телефон <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseInput
            v-model="formData.phone"
            id="phone"
            :hasError="hasFormError('phone')"
            :disabled="isDisabled('phone')"
          />
          <BaseError v-if="hasFormError('phone')">
            {{ getFormError('phone') }}
          </BaseError>
        </div>
        <div class="w-1/3">
          <BaseLabel for-id="location">
            Субъект РФ <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseInput
            v-model="formData.location"
            id="location"
            :hasError="hasFormError('location')"
            :disabled="isDisabled('location')"
          />
          <BaseError v-if="hasFormError('location')">
            {{ getFormError('location') }}
          </BaseError>
        </div>
      </div>

      <div class="mb-2 flex flex-row w-5/6">
        <div class="dictionary-wrapper mr-6">
          <BaseLabel for-id="dictionary">
            Выбор справочника <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseSelect
            id="dictionary"
            :options="dictionarySelect"
            :reduce="(item) => item.id"
            v-model="formData.dictionary"
            :hasError="hasFormError('dictionary')"
            :disabled="isDisabled('dictionary')"
          />
          <BaseError v-if="hasFormError('dictionary')">
            {{ getFormError('dictionary') }}
          </BaseError>
        </div>

        <div class="w-2/3" v-if="isConditionMode">
          <BaseLabel for-id="condition_name">
            Наименование условия <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseTextarea
            v-model="formData.conditionName"
            id="value"
            :resize="false"
            :hasError="hasFormError('condition_name')"
            :disabled="isDisabled('condition_name')"
          />
          <BaseError v-if="hasFormError('condition_name')">
            {{ getFormError('condition_name') }}
          </BaseError>
        </div>
        <div class="w-2/3" v-else>
          <BaseLabel for-id="value">
            Новое значение <span class="text-trueBlue">*</span>
          </BaseLabel>
          <BaseInput
            v-model="formData.value"
            id="value"
            :hasError="hasFormError('value')"
            :disabled="isDisabled('value')"
          />
          <BaseError v-if="hasFormError('value')">
            {{ getFormError('value') }}
          </BaseError>
        </div>
      </div>

      <div class="mb-2 flex flex-row w-5/6">
        <div class="dictionary-wrapper mr-6">
          <BaseLabel for-id="life_situation_id">
            Жизненная ситуация <span class="text-trueBlue">*</span>
          </BaseLabel>
          <LazySelect
            id="life_situation_id"
            label="name"
            :source="loadLifeSituation"
            v-model="formData.lifeSituation"
            :hasError="hasFormError('life_situation_id')"
            :disabled="isDisabled('life_situation_id')"
          />
          <BaseError v-if="hasFormError('life_situation_id')">
            {{ getFormError('life_situation_id') }}
          </BaseError>
        </div>
      </div>

      <div class="mb-2 w-5/6" v-if="isConditionMode">
        <BaseLabel for-id="condition_question">
          Формулировка вопроса для отображения в анкете гражданину
          <span class="text-trueBlue">*</span>
        </BaseLabel>
        <BaseTextarea
          v-model="formData.conditionQuestion"
          id="condition_question"
          :resize="false"
          :hasError="hasFormError('condition_question')"
          :disabled="isDisabled('condition_question')"
        />
        <BaseError v-if="hasFormError('condition_question')">
          {{ getFormError('condition_question') }}
        </BaseError>
      </div>

      <template v-if="!isConditionMode">
        <div
          class="mb-2 w-5/6 relative"
          v-for="(lmsp, index) in formData.lmspList"
          :key="index"
        >
          <div class="absolute right-0" v-if="index !== 0">
            <BaseButton
              variant="none"
              class="text-metallicSilver relative remove-lmsp-btn"
              @click="removeLmsp(index)"
            >
              <BaseIcon name="close" width="12" height="12" />
            </BaseButton>
          </div>

          <BaseLabel :for-id="`uuid-${index}`">
            Идентификатор ЛМСЗ в ЕГИССО (UUID)
          </BaseLabel>
          <div class="flex flex-col mb-2">
            <div class="flex flex-row">
              <div class="w-1/2 mr-3">
                <BaseInput
                  v-model="lmsp.uuid"
                  :id="`uuid-${index}`"
                  :hasError="hasLmspError('lmsp', index)"
                  :disabled="isDisabled('lmsp_list')"
                />
              </div>
              <BaseButton
                size="xs"
                variant="blue-transparent"
                :disabled="isLoading || isDisabled('lmsp_list')"
                @click="findLMSP(lmsp)"
              >
                <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
                Найти
              </BaseButton>
            </div>
            <BaseError v-if="hasLmspError('lmsp', index)">
              {{ getLmspError('lmsp', index) }}
            </BaseError>
          </div>

          <BaseLabel for-id="`name-${index}`">Наименование ЛМСЗ</BaseLabel>
          <BaseInput
            v-model="lmsp.name"
            :id="`name-${index}`"
            :disabled="true"
          />
        </div>
        <BaseButton
          size="sm"
          variant="blue-transparent"
          class="mt-2"
          @click="addLmsp"
          :disabled="isDisabled('lmsp_list')"
        >
          Добавить ЛМСЗ
        </BaseButton>
      </template>

      <div class="w-5/6 mt-6">
        <BaseLabel for-id="comment">
          Комментарий <span class="text-trueBlue">*</span>
        </BaseLabel>
        <p v-if="!isConditionMode" class="text-sm text-sonicSilver mb-2">
          Комментарий должен содержать ссылки на НПА и описывать, как
          определются условия возникновения жизненного события согласно НПА
        </p>
        <BaseTextarea
          v-model="formData.comment"
          id="comment"
          :hasError="hasFormError('comment')"
          :disabled="isDisabled('comment')"
        />
        <BaseError v-if="hasFormError('comment')">
          {{ getFormError('comment') }}
        </BaseError>
      </div>
    </div>

    <template v-if="showAdditionalForm">
      <div class="px-6 mt-6 w-5/6 flex flex-row">
        <div class="w-1/2">
          <BaseLabel for-id="status"> Статус Минтруд России </BaseLabel>
          <div>{{ getAcceptedName(formData.mintrudAccepted) }}</div>
        </div>
        <div class="w-1/2">
          <BaseLabel for-id="status"> Статус ПФР </BaseLabel>
          <div>{{ getAcceptedName(formData.pfrAccepted) }}</div>
        </div>
      </div>
      <div class="px-6" v-if="isOperator">
        <div class="w-5/6 mt-6">
          <BaseLabel for-id="status"> Статус </BaseLabel>
          <LazySelect
            id="status"
            label="name"
            :source="loadStatuses"
            v-model="formData.status"
            :hasError="hasFormError('status_id')"
            :disabled="isDisabled('status_id')"
          />
          <BaseError v-if="hasFormError('status_id')">
            {{ getFormError('status_id') }}
          </BaseError>
        </div>
        <div class="w-5/6 mt-6">
          <BaseLabel for-id="answer">Ответ</BaseLabel>
          <BaseTextarea
            v-model="formData.answer"
            id="answer"
            :hasError="hasFormError('answer')"
          />
          <BaseError v-if="hasFormError('answer')">
            {{ getFormError('answer') }}
          </BaseError>
        </div>
      </div>

      <Divider class="my-6" />

      <div class="px-6">
        <div class="mb-2 mt-6 font-bold">Комментарии</div>
        <CommentList :source="formData.id" type="dictionary_req" />
      </div>
    </template>

    <Divider class="my-6" />

    <div class="mb-6 px-6" v-if="!isPfrOrMintrud">
      <BaseButton
        class="mr-6"
        size="sm"
        variant="blue"
        :disabled="isLoading"
        @click="onSave"
      >
        <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
        <span v-if="formData.id"> Сохранить </span>
        <span v-else> Отправить </span>
      </BaseButton>
    </div>
    <div v-else class="mb-6 px-6">
      <BaseButton
        class="mr-6"
        size="sm"
        variant="blue"
        :disabled="isLoading"
        @click="onAccept(true)"
      >
        <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
        <span> Согласовать добавление </span>
      </BaseButton>
      <BaseButton
        size="sm"
        variant="blue-transparent"
        :disabled="isLoading"
        @click="onAccept(false)"
      >
        <BaseLoader v-if="isLoading" size="xs" class="mr-1" />
        <span> Отклонить </span>
      </BaseButton>
    </div>
  </form>
</template>

<script>
  import {mapGetters} from 'vuex';

  import Client from '@/classes/Api/Client';
  import WithHttpError from '@/components/mixins/WithHttpError';

  import BaseIcon from '@/components/global/base-icon';
  import Divider from '@/components/global/base-divider';
  import BaseLabel from '@/components/global/base-label';
  import BaseInput from '@/components/global/base-input';
  import BaseError from '@/components/global/base-error';
  import BaseSelect from '@/components/global/base-select';
  import LazySelect from '@/components/global/lazy-select';
  import BaseButton from '@/components/global/base-button';
  import BaseLoader from '@/components/global/base-loader';
  import CommentList from '@/components/global/comment-list';
  import BaseTextarea from '@/components/global/base-textarea';

  import {dictionaryCategories} from '@/utils/dictionaryCategories';
  import {getOperatorStatusName} from '@/utils/dictionaryOperatorStatus';

  export default {
    name: 'DictionaryForm',

    components: {
      Divider,
      BaseIcon,
      BaseLabel,
      BaseInput,
      BaseError,
      BaseSelect,
      LazySelect,
      BaseButton,
      BaseLoader,
      CommentList,
      BaseTextarea,
    },

    mixins: [WithHttpError],

    props: {
      data: {
        type: Object,
        default: () => ({}),
      },
      disabledFields: {
        type: Array,
        default: () => [],
      },
    },

    data() {
      return {
        formData: {
          id: null,
          fio: null,
          email: null,
          phone: null,
          value: null,
          conditionName: null,
          conditionQuestion: null,
          status: null,
          position: null,
          location: null,
          dictionary: 'condition',
          lifeSituation: null,
          lmspList: [{uuid: null, name: null}],
          comment: null,
          answer: null,
          pfrAccepted: null,
          mintrudAccepted: null,
        },

        dictionarySelect: dictionaryCategories(),

        isLoading: false,
      };
    },

    computed: {
      ...mapGetters('Auth', ['hasRole']),

      isOperator() {
        return this.hasRole('operator');
      },

      isPfr() {
        return this.hasRole('pfr');
      },

      isMintrud() {
        return this.hasRole('mintrud');
      },

      isPfrOrMintrud() {
        return this.isPfr || this.isMintrud;
      },

      showAdditionalForm() {
        return this.isOperator || this.isPfrOrMintrud;
      },

      isConditionMode() {
        return this.formData.dictionary === 'condition';
      },
    },

    created() {
      this.bindFormData();
    },

    watch: {
      data() {
        this.bindFormData();
      },
    },

    methods: {
      bindFormData() {
        this.formData = {
          id: this.data.id || null,
          fio: this.data.fio || null,
          email: this.data.email || null,
          phone: this.data.phone || null,
          value: this.data.value || null,
          conditionName: this.data.condition_name || null,
          conditionQuestion: this.data.condition_question || null,
          lifeSituation: this.data.life_situation || null,
          status: this.data.status || null,
          position: this.data.position || null,
          location: this.data.location || null,
          dictionary: this.data.dictionary || 'condition',
          lmspList: this.data.lmsp_list || [{uuid: null, name: null}],
          comment: this.data.comment || null,
          answer: this.data.answer || null,
          pfrAccepted: this.data.pfr_accepted,
          mintrudAccepted: this.data.mintrud_accepted,
        };
      },

      isDisabled(name) {
        return this.disabledFields.indexOf(name) !== -1;
      },

      getAcceptedName(val) {
        return getOperatorStatusName(val);
      },

      loadStatuses() {
        return Client.getDictionaryReqStatuses().then((response) => {
          return response.data;
        });
      },

      loadLifeSituation() {
        return Client.getLifeSituations().then((response) => {
          return response.data;
        });
      },

      addLmsp() {
        this.formData.lmspList.push({uuid: null, name: null});
      },

      removeLmsp(index) {
        this.removeLmspError(index);
        this.formData.lmspList.splice(index, 1);
      },

      hasLmspError(group, index) {
        return typeof this.formError[`${group}.${index}`] !== 'undefined';
      },

      getLmspError(group, index) {
        return this.getFormError(`${group}.${index}`);
      },

      removeLmspError(index) {
        if (typeof this.formError[`lmsp.${index}.uuid`] !== 'undefined') {
          delete this.formError[`lmsp.${index}.uuid`];
        }
        if (typeof this.formError[`lmsp.${index}.name`] !== 'undefined') {
          delete this.formError[`lmsp.${index}.name`];
        }
      },

      findLMSP(lmsp) {
        if (!(!this.isLoading && (this.isLoading = true))) {
          return;
        }

        return Client.getLMSPInfo(lmsp.uuid)
          .then((response) => {
            let data = response.data;
            lmsp.name = data.lmsp_name;
          })
          .catch(this.prepareHttpError)
          .finally(() => {
            this.isLoading = false;
          });
      },

      onAccept(status) {
        this.save({
          accepted: status,
        });
      },

      onSave() {
        this.save({});
      },

      save(additionParams) {
        if (!(!this.isLoading && (this.isLoading = true))) {
          return;
        }

        let data = Object.assign(additionParams || {}, {
          fio: this.formData.fio,
          email: this.formData.email,
          phone: this.formData.phone,
          value: this.formData.value,
          condition_name: this.formData.conditionName,
          condition_question: this.formData.conditionQuestion,
          life_situation_id:
            this.formData.lifeSituation && this.formData.lifeSituation.id,
          status_id: this.formData.status && this.formData.status.id,
          position: this.formData.position,
          location: this.formData.location,
          dictionary: this.formData.dictionary,
          comment: this.formData.comment,
          answer: this.formData.answer,
          lmsp: this.formData.lmspList
            .map((item) => {
              return item.uuid;
            })
            .filter((item) => {
              return item !== null;
            }),
        });

        if (data.condition_name) {
          data.condition_name = data.condition_name.replace(/\n/g, ' ');
        }

        if (data.condition_question) {
          data.condition_question = data.condition_question.replace(/\n/g, ' ');
        }

        let request = this.formData.id
          ? Client.updateOperatorDictionaryRequest(this.formData.id, data)
          : Client.saveDictionaryRequest(data);

        return request
          .then(() => {
            if (this.formData.id) {
              if (this.isPfr) {
                this.formData.pfrAccepted = additionParams.accepted;
              }
              if (this.isMintrud) {
                this.formData.mintrudAccepted = additionParams.accepted;
              }
              this.$store.commit('Notification/showAlert', {
                title: 'Заявка успешно обновлена!',
              });
            } else {
              this.formData = {
                id: null,
                fio: null,
                email: null,
                phone: null,
                value: null,
                conditionName: null,
                conditionQuestion: null,
                lifeSituation: null,
                status: null,
                dictionary: this.formData.dictionary,
                position: null,
                location: null,
                lmspList: [{uuid: null, name: null}],
                comment: null,
                answer: null,
                pfrAccepted: null,
                mintrudAccepted: null,
              };

              this.$store.commit('Notification/showAlert', {
                title: 'Заявка успешно создана!',
                message: 'Ваша заявка отправлена, скоро мы ее рассмотрим.',
              });
            }

            this.clearFormErrors();
          })
          .catch(this.prepareHttpError)
          .catch(() => {
            this.$store.commit('Notification/showAlert', {
              title: 'Ошибка!',
              message:
                'Произошла ошибка, пожалуйста попробуйте повторно отпраивть заявку.',
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .dictionary-wrapper {
    width: 32%;
  }
  .remove-lmsp-btn {
    left: rem(15px);
  }
</style>
